import { graphql } from 'react-apollo';

import { useQuery } from '@apollo/client';
import type {
  EnrollmentChoiceQueries,
  EnrollmentChoiceQueriesEnrollmentChoicesArgs,
} from '__generated__/graphql-types-do-not-use';
import { compose, withProps } from 'recompose';

import logger from 'js/app/loggerSingleton';
import user from 'js/lib/user';
import waitForGraphql from 'js/lib/waitForGraphQL';

import EnrollmentChoiceQuery from 'bundles/enroll-common/api/EnrollmentChoiceQuery.graphql';
import EnrollmentChoicesDGS from 'bundles/enroll-common/models/EnrollmentChoices';
import { SPECIALIZATION, VERIFIED_CERTIFICATE } from 'bundles/payments-common/common/ProductType';
import type { PropsFromWithPromotionInfo } from 'bundles/promotions/components/withPromotionInfo';

type PropsToHOC = {
  isSpecialization?: boolean;
  s12nId?: string;
  courseId?: string;
  partnerIds?: Array<string>;
} & Partial<PropsFromWithPromotionInfo>;

type PropsFromWithProps = {
  userId: number;
  productItemId?: string;
  productType: string;
  partnerIds?: Array<string>;
};

export type EnrollmentChoices = EnrollmentChoicesDGS;

export type PropsFromWithEnrollment = {
  enrollmentAvailableChoices?: EnrollmentChoices;
  isLoadingEnrollmentChoices?: boolean;
  partnerIds?: Array<string>;
};

type EnrollmentChoiceQueriesType = {
  __typename?: 'Query';
  EnrollmentChoice: EnrollmentChoiceQueries;
};

type UseEnrollmentProps = {
  isSpecialization?: boolean;
  s12nId?: string;
  courseId?: string;
  partnerIds?: Array<string>;
  isNotRequired?: boolean;
  skip?: boolean;
};

export type UseEnrollmentResult = {
  enrollmentAvailableChoices?: EnrollmentChoices;
  isLoadingEnrollmentChoices: boolean;
  error?: Error;
};

export const useEnrollment = ({
  isSpecialization,
  s12nId,
  courseId,
  skip,
}: UseEnrollmentProps): UseEnrollmentResult => {
  // Only get enrollment from s12nId, instead of courseId, if the `isSpecialization` flag is passed
  // or if the courseId is not available; getting from courseId is preferred otherwise
  // because it returns more enrollment choices
  const useS12nId = isSpecialization || Boolean(s12nId && !courseId);
  const productItemId = useS12nId ? s12nId : courseId;
  const productType = useS12nId ? SPECIALIZATION : VERIFIED_CERTIFICATE;

  const { data, loading, error } = useQuery<EnrollmentChoiceQueriesType, EnrollmentChoiceQueriesEnrollmentChoicesArgs>(
    EnrollmentChoiceQuery,
    {
      skip: !user.isAuthenticatedUser() || !productItemId || skip,
      variables: {
        userId: user.get().id,
        productType,
        productItemId: productItemId as string,
        includeProgramInvitationCheck: true,
      },
      context: { clientName: 'gatewayGql' },
    }
  );

  if (error) {
    logger.error('Unable to fetch GetEnrollmentChoices query');
  }

  return {
    isLoadingEnrollmentChoices: loading,
    enrollmentAvailableChoices: data?.EnrollmentChoice
      ? new EnrollmentChoicesDGS(data.EnrollmentChoice.enrollmentChoices)
      : undefined,
    error,
  };
};

const withEnrollment = <InputProps extends PropsToHOC>(isNotRequired?: boolean) => {
  const graphqlHoc = !isNotRequired ? waitForGraphql : graphql;

  return compose(
    withProps<PropsFromWithProps, InputProps>(({ isSpecialization, s12nId, courseId, partnerIds }) => {
      // Only get enrollment from s12nId, instead of courseId, if the `isSpecialization` flag is passed
      // or if the courseId is not available; getting from courseId is preferred otherwise
      // because it returns more enrollment choices
      const useS12nId = isSpecialization || Boolean(s12nId && !courseId);
      const productItemId = useS12nId ? s12nId : courseId;
      const productType = useS12nId ? SPECIALIZATION : VERIFIED_CERTIFICATE;

      return {
        userId: user.get().id,
        productItemId,
        productType,
        partnerIds,
      };
    }),
    graphqlHoc<
      Required<PropsFromWithProps> & PropsToHOC,
      EnrollmentChoiceQueriesType,
      EnrollmentChoiceQueriesEnrollmentChoicesArgs,
      PropsFromWithEnrollment
    >(EnrollmentChoiceQuery, {
      skip: ({ productItemId }) => !user.isAuthenticatedUser() || !productItemId,
      options: ({ productItemId, productType, userId }) => ({
        variables: {
          userId,
          productType,
          productItemId,
          // We're defaulting includeProgramInvitationCheck to true to avoid updating all the call-sites and making it possible
          // to accidentally add new usages that don't send this flag. If we don't do this, we'll end up with multiple requests
          // with and without the parameter to this high-latency resource.
          includeProgramInvitationCheck: true,
        },
        context: { clientName: 'gatewayGql' },
        errorPolicy: isNotRequired ? 'all' : undefined,
      }),

      props: ({ data }) => {
        if (data?.error) {
          logger.error('Unable to fetch GetEnrollmentChoices query');
        }

        return {
          isLoadingEnrollmentChoices: data?.loading ?? false,
          ...(data?.EnrollmentChoice
            ? {
                enrollmentAvailableChoices: new EnrollmentChoicesDGS(data.EnrollmentChoice.enrollmentChoices),
              }
            : {}),
        };
      },
    })
  );
};

export default withEnrollment;
